import { useState } from "react";
import {
  Button,
  Label,
  ListBox,
  Select,
} from "@heroui/react";
import { MealSlot, SlotException } from "../api/client";
import { DAY_LABELS_FULL, SLOT_ROWS } from "../constants";

interface Props {
  slotDefaults: MealSlot[];
  value: SlotException[] | undefined;
  onChange: (next: SlotException[]) => void;
}

const DAYS = [1, 2, 3, 4, 5, 6, 7];

export function SlotExceptionsEditor({ slotDefaults, value, onChange }: Props) {
  const safeValue = Array.isArray(value) ? value : [];
  const [adding, setAdding] = useState(false);
  const [pickDay, setPickDay] = useState<number>(1);
  const [pickSlot, setPickSlot] = useState<MealSlot>("lunch");

  function existing(day: number, slot: MealSlot) {
    return safeValue.some((e) => e.day === day && e.mealSlot === slot);
  }

  function addException() {
    if (existing(pickDay, pickSlot)) {
      setAdding(false);
      return;
    }
    onChange([...safeValue, { day: pickDay, mealSlot: pickSlot }]);
    setAdding(false);
  }

  function remove(day: number, slot: MealSlot) {
    onChange(safeValue.filter((e) => !(e.day === day && e.mealSlot === slot)));
  }

  const sorted = [...safeValue].sort((a, b) => {
    if (a.day !== b.day) return a.day - b.day;
    return SLOT_ROWS.findIndex((r) => r.slot === a.mealSlot) - SLOT_ROWS.findIndex((r) => r.slot === b.mealSlot);
  });

  return (
    <div className="space-y-3 rounded-2xl border border-default-200 bg-surface p-4 shadow-sm">
      <div className="space-y-1">
        <div className="text-sm font-semibold text-foreground">Excepcions setmanals</div>
        <div className="text-xs leading-relaxed text-muted">
          Inverteixen l&apos;estat per defecte només per a un dia concret.
        </div>
      </div>

      {sorted.length === 0 && !adding && (
        <div className="rounded-md bg-surface-secondary px-3 py-2 text-xs text-muted">
          Cap excepció configurada.
        </div>
      )}

      {sorted.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {sorted.map((e) => {
            const slotLabel = SLOT_ROWS.find((r) => r.slot === e.mealSlot)?.label ?? e.mealSlot;
            const inDefault = slotDefaults.includes(e.mealSlot);
            const effect = inDefault ? "queda fora" : "queda dins";
            const effectClass = inDefault ? "text-danger" : "text-success";
            return (
              <li
                key={`${e.day}-${e.mealSlot}`}
                className="flex items-center gap-2 rounded-md bg-surface-secondary px-2.5 py-1.5 text-sm"
              >
                <span className="font-medium">{DAY_LABELS_FULL[e.day]}</span>
                <span className="text-muted">·</span>
                <span>{slotLabel}</span>
                <span className="text-muted">·</span>
                <span className={`font-medium ${effectClass}`}>{effect}</span>
                <button
                  type="button"
                  onClick={() => remove(e.day, e.mealSlot)}
                  className="ml-auto text-xs text-muted hover:text-danger"
                  aria-label="Treu l'excepció"
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {adding ? (
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-default-200 p-2">
          <Select
            className="min-w-[140px]"
            selectedKey={String(pickDay)}
            onSelectionChange={(k) => setPickDay(Number(k))}
          >
            <Label className="text-xs">Dia</Label>
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {DAYS.map((d) => (
                  <ListBox.Item key={d} id={String(d)} textValue={DAY_LABELS_FULL[d]}>
                    {DAY_LABELS_FULL[d]}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
          <Select
            className="min-w-[180px]"
            selectedKey={pickSlot}
            onSelectionChange={(k) => setPickSlot(k as MealSlot)}
          >
            <Label className="text-xs">Franja</Label>
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {SLOT_ROWS.map((r) => (
                  <ListBox.Item key={r.slot} id={r.slot} textValue={r.label}>
                    {r.label}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
          <Button size="sm" onPress={addException}>
            Afegeix
          </Button>
          <Button size="sm" variant="tertiary" onPress={() => setAdding(false)}>
            Cancel·la
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="secondary" onPress={() => setAdding(true)}>
          + Afegeix excepció
        </Button>
      )}
    </div>
  );
}
